import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type ActiveBanner,
  dismissBanner,
  fetchActiveBanners,
} from '../api/banners'

export const ACTIVE_BANNERS_KEY: readonly ['banners', 'active'] = ['banners', 'active']

/**
 * Fetch active banners for the current viewer. Mirrors usePublicConfig's
 * resilience: on error it returns an empty list (never throws into render) so a
 * banner/API outage can never break the app — the env MaintenanceBanner remains
 * the outage-proof fallback channel.
 */
export function useActiveBanners(): ActiveBanner[] {
  const query = useQuery({
    queryKey: ACTIVE_BANNERS_KEY,
    queryFn: fetchActiveBanners,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
  return query.data ?? []
}

/** Persist a one-time dismissal server-side (authenticated users). */
export function useDismissBanner() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (bannerId: string) => dismissBanner(bannerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACTIVE_BANNERS_KEY })
    },
  })
}
